import { i18n, createInstance } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend, { LocizeBackendOptions } from 'i18next-locize-backend';
import LocizeLastusedPlugin, { LocizeLastusedOptions } from 'locize-lastused';
import { memoize } from 'lodash';
import { ReactNode, useEffect, useState } from 'react';
import { I18nextProvider as I18nProvider } from 'react-i18next';

const isHostAllowed = (hostname: string) =>
  ['localhost', 'app.stage.cnxx.io'].includes(hostname) ||
  hostname.endsWith('.cnxx.io');

const createI18n = memoize(
  (namespace: string, projectId: string, apiKey: string) =>
    new Promise<i18n>((resolve) => {
      const i18n = createInstance()
        .use(Backend)
        .use(LocizeLastusedPlugin)
        .use(LanguageDetector);
      i18n
        .init<LocizeBackendOptions & LocizeLastusedOptions>({
          ns: [namespace, 'translations'],
          defaultNS: namespace,
          backend: {
            projectId,
            apiKey,
            referenceLng: 'en',
            private: false,
            allowedAddOrUpdateHosts: isHostAllowed,
          },
          locizeLastUsed: {
            projectId,
            apiKey,
            allowedHosts: ['localhost', 'app.stage.cnxx.io'],
          },
          fallbackLng: 'en',
          debug: process.env.NODE_ENV === 'development',
          react: { useSuspense: false },
          supportedLngs: ['en', 'de', 'pl'],
          interpolation: { escapeValue: false },
          load: 'languageOnly',
          saveMissing: true,
          returnEmptyString: false,
          missingKeyHandler: (...args) => {
            const [lng, ns, key, fallbackValue, one, two] = args;
            if (!key || key.trim() === '' || key.includes(' ')) {
              throw new Error(
                `Missing translation for ${key} in ${ns} in ${lng}. Please check if the key contains spaces.`,
              );
            }
            if (process.env.CI) {
              throw new Error(
                `Missing translation for ${key} in ${ns} in ${lng}. Please add the key to locize.`,
              );
            }
            i18n.services.backendConnector.saveMissing(
              lng,
              ns,
              key,
              fallbackValue === key ? null : fallbackValue,
              one,
              two,
            );
            console.warn(
              `Added missing translation for ${key} in ${ns} in ${lng}`,
            );
          },
        })
        // eslint-disable-next-line no-console
        .catch((error: Error) => console.error(error));

      i18n.on('initialized', () => {
        resolve(i18n);
      });
    }),
);

export interface I18nProps {
  children: ReactNode;
  config: I18nConfig;
}

export type I18nConfig = {
  namespace: string;
  locizeProjectId: string;
  locizeApiKey: string;
};

export function I18nProviderWrapper({ children, config }: I18nProps) {
  const [i18n, setI18n] = useState<i18n | null>(null);

  const { namespace, locizeProjectId, locizeApiKey } = config;
  useEffect(() => {
    let cancelled = false;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    createI18n(namespace, locizeProjectId, locizeApiKey).then((i18n) => {
      if (!cancelled) setI18n(i18n);
    });
    return () => {
      cancelled = true;
    };
  }, [namespace, locizeProjectId, locizeApiKey]);

  return i18n && <I18nProvider i18n={i18n}>{children}</I18nProvider>;
}
